<script lang="ts">
  import type { TonoCielo } from '@/types';

  interface Props {
    tono: TonoCielo;
    intensidadAgudo?: number;
    intensidadMedio?: number;
    intensidadBajo?: number;
  }

  let {
    tono,
    intensidadAgudo = 0,
    intensidadMedio = 0,
    intensidadBajo = 0,
  }: Props = $props();

  const TOPE_POR_TONO: Record<TonoCielo, string> = {
    noche: '#0c1220',
    crepusculo: '#29213d',
    amanecer: '#d9a85c',
    papel: '#f4ead5',
  };

  const BASE_POR_TONO: Record<TonoCielo, string> = {
    noche: '#080d18',
    crepusculo: '#121626',
    amanecer: '#4a3b6a',
    papel: '#f4ead5',
  };

  // Generador pseudoaleatorio determinista para una distribución celestial natural sin patrones visibles
  function mulberry32(seed: number) {
    return function() {
      let t = (seed += 0x6d2b79f5);
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  const rng = mulberry32(921);
  const ESTRELLAS = Array.from({ length: 55 }, (_, i) => {
    return {
      id: i,
      // Dispersión por todo el domo del cielo (desde 4% a 78% de altura, 3% a 97% horizontal)
      top: (rng() * 74 + 4).toFixed(1),
      left: (rng() * 94 + 3).toFixed(1),
      baseSize: (rng() * 2.5 + 1.5).toFixed(1),
      esGrande: i % 7 === 0, // Algunas estrellas lucen fulgor de diamante
      tipoBanda: i % 4 === 0 ? 'agudo' : i % 4 === 1 ? 'medio' : i % 4 === 2 ? 'bajo' : 'global',
      delayAnim: (rng() * 3).toFixed(2),
    };
  });
</script>

<div
  class="cielo"
  class:cielo--noche={tono === 'noche'}
  class:cielo--crepusculo={tono === 'crepusculo'}
  class:cielo--amanecer={tono === 'amanecer'}
  class:cielo--papel={tono === 'papel'}
  style="--tope-cielo: {TOPE_POR_TONO[tono]}; --base-cielo: {BASE_POR_TONO[tono]};"
>
  <!-- Nubes atmosféricas suaves estilo acuarela Ghibli -->
  <div class="nubes-contenedor" aria-hidden="true">
    <div class="nube nube-1"></div>
    <div class="nube nube-2"></div>
    <div class="nube nube-3"></div>
  </div>

  <!-- Estrellas parpadeantes audio-reactivas al ritmo de la música -->
  <div class="manto-estrellas" aria-hidden="true">
    {#each ESTRELLAS as e (e.id)}
      {@const reactividad =
        e.tipoBanda === 'agudo'
          ? intensidadAgudo
          : e.tipoBanda === 'medio'
            ? intensidadMedio
            : e.tipoBanda === 'bajo'
              ? intensidadBajo
              : (intensidadAgudo + intensidadBajo) * 0.5}
      <span
        class="estrella-musical"
        class:estrella-diamante={e.esGrande}
        style="
          top: {e.top}%;
          left: {e.left}%;
          width: {e.baseSize}px;
          height: {e.baseSize}px;
          --escala-musica: {(1 + reactividad * 2.2).toFixed(2)};
          --brillo-musica: {(0.2 + reactividad * 0.85).toFixed(2)};
          --delay: {e.delayAnim}s;
        "
      ></span>
    {/each}

    <!-- Estrellas fugaces ocasionales estilo Kimi no Na wa / Ghibli -->
    <div class="estrella-fugaz fugaz-1"></div>
    <div class="estrella-fugaz fugaz-2"></div>
  </div>

  <!-- Sol del crepúsculo: inicia desde la mitad y cae pausadamente detrás del horizonte -->
  <div class="sol-crepusculo" aria-hidden="true">
    <div class="sol-corona"></div>
    <div class="sol-disco"></div>
  </div>

  <!-- Luna suave con aura plateada/dorada -->
  {#if tono === 'noche' || tono === 'crepusculo'}
    <div class="luna-ghibli" aria-hidden="true">
      <div class="luna-halo"></div>
      <div class="luna-disco"></div>
    </div>
  {/if}

  <!-- Manto de Amanecer que sube desde abajo devorando la noche -->
  <div
    class="ola-amanecer-ascendente"
    class:ola-activa={tono === 'amanecer'}
    aria-hidden="true"
  >
    <div class="gradiente-luz-ascendente"></div>
    <div class="aurora-dorada-borde"></div>
    <!-- Sol naciente que emerge desde el horizonte subiendo con la luz -->
    <div class="sol-naciente-amanecer">
      <div class="sol-naciente-corona"></div>
      <div class="sol-naciente-disco"></div>
    </div>
  </div>

  <!-- Fondo Ghibli mimetizado con la luz del amanecer -->
  <div
    class="fondo-ghibli-paisaje"
    class:paisaje-visible={tono === 'amanecer'}
    aria-hidden="true"
  >
    <img
      src="/images/fondo-ghibli.jpg"
      alt="Paisaje Ghibli al amanecer"
      class="img-fondo-ghibli"
    />
    <div class="filtro-mimetizado-luz"></div>
  </div>

  <!-- Halo de resplandor sobre el horizonte -->
  <div class="resplandor-horizonte" class:horizonte-amanecer={tono === 'amanecer'} aria-hidden="true"></div>
</div>

<style>
  .cielo {
    position: fixed;
    inset: 0;
    z-index: 0;
    background: linear-gradient(180deg, var(--tope-cielo) 0%, var(--base-cielo) 100%);
    overflow: hidden;
  }

  /* La gran ola de luz del amanecer que sube desde el horizonte devorando la noche */
  .ola-amanecer-ascendente {
    position: absolute;
    inset: 0;
    z-index: 2;
    pointer-events: none;
    transform: translateY(105%);
    transition: transform 4.8s cubic-bezier(0.22, 1, 0.36, 1);
    will-change: transform;
  }

  .ola-amanecer-ascendente.ola-activa {
    transform: translateY(0%);
  }

  /* Gradiente matutino exuberante: ambar, oro, rosa crepúsculo y azul de día */
  .gradiente-luz-ascendente {
    position: absolute;
    inset: 0;
    background: linear-gradient(
      180deg,
      transparent 0%,
      rgba(107, 127, 163, 0.5) 15%,
      rgba(217, 168, 92, 0.85) 45%,
      rgba(255, 220, 140, 0.95) 75%,
      #ffd68a 100%
    );
  }

  /* Frente de onda brillante: un halo dorado en la cresta de la luz que barre hacia arriba */
  .aurora-dorada-borde {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 120px;
    background: linear-gradient(
      180deg,
      rgba(255, 235, 170, 0.9) 0%,
      rgba(242, 177, 53, 0.6) 50%,
      transparent 100%
    );
    filter: blur(15px);
    box-shadow: 0 0 50px rgba(255, 215, 110, 0.8);
  }

  /* Sol naciente que sube impulsado por la ola de amanecer */
  .sol-naciente-amanecer {
    position: absolute;
    top: 25%;
    left: 50%;
    transform: translateX(-50%);
    width: 130px;
    height: 130px;
    opacity: 0;
    transition: opacity 2s ease;
    transition-delay: 1.5s;
  }

  .ola-activa .sol-naciente-amanecer {
    opacity: 1;
  }

  .sol-naciente-disco {
    position: absolute;
    inset: 15px;
    border-radius: 50%;
    background: radial-gradient(circle, #ffffff 0%, #fff0a8 40%, #f5b031 80%, #df6d22 100%);
    box-shadow:
      0 0 40px rgba(255, 240, 160, 0.9),
      0 0 80px rgba(245, 176, 49, 0.6);
  }

  .sol-naciente-corona {
    position: absolute;
    inset: -20px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255, 235, 150, 0.5) 0%, transparent 75%);
    filter: blur(16px);
    animation: pulsoCoronaAmanecer 3.5s ease-in-out infinite alternate;
  }

  @keyframes pulsoCoronaAmanecer {
    0% { transform: scale(0.92); opacity: 0.6; }
    100% { transform: scale(1.12); opacity: 1; }
  }

  /* Fondo Ghibli al amanecer, suavemente mimetizado tras concluir el barrido del amanecer */
  .fondo-ghibli-paisaje {
    position: absolute;
    inset: 0;
    z-index: 3;
    opacity: 0;
    pointer-events: none;
    transition: opacity 2.8s cubic-bezier(0.25, 1, 0.5, 1);
  }

  .fondo-ghibli-paisaje.paisaje-visible {
    opacity: 0.65; /* Mimetizado sutilmente con el cielo matutino */
    transition-delay: 5s; /* Aparece únicamente DESPUÉS de que la ola de amanecer cubrió la noche */
  }

  .img-fondo-ghibli {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center bottom;
    filter: saturate(1.2) contrast(1.05) brightness(1.02);
  }

  /* Capa de luz matutina dorada y etérea sobre la imagen */
  .filtro-mimetizado-luz {
    position: absolute;
    inset: 0;
    background: linear-gradient(
      180deg,
      rgba(217, 168, 92, 0.35) 0%,
      rgba(255, 235, 160, 0.2) 50%,
      rgba(74, 59, 106, 0.35) 100%
    );
    mix-blend-mode: overlay;
  }

  /* Nubes crepusculares */
  .nubes-contenedor {
    position: absolute;
    inset: 0;
    pointer-events: none;
    opacity: 0.35;
  }

  .nube {
    position: absolute;
    border-radius: 9999px;
    background: linear-gradient(180deg, rgba(242, 177, 53, 0.2) 0%, rgba(154, 98, 166, 0.1) 100%);
    filter: blur(28px);
  }

  .nube-1 {
    top: 20%;
    left: -10%;
    width: 60vw;
    height: 120px;
    animation: flotarNube 35s linear infinite alternate;
  }

  .nube-2 {
    top: 40%;
    right: -15%;
    width: 70vw;
    height: 140px;
    animation: flotarNube 42s linear infinite alternate-reverse;
  }

  .nube-3 {
    top: 60%;
    left: 10%;
    width: 50vw;
    height: 100px;
    animation: flotarNube 30s linear infinite alternate;
  }

  @keyframes flotarNube {
    0% { transform: translateX(0); }
    100% { transform: translateX(12%); }
  }

  /* Estrellas musicales reactivas al audio */
  .manto-estrellas {
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 1;
    transition: opacity 3s cubic-bezier(0.25, 1, 0.5, 1);
  }

  .cielo--amanecer .manto-estrellas,
  .cielo--papel .manto-estrellas {
    opacity: 0;
  }

  .estrella-musical {
    position: absolute;
    border-radius: 50%;
    background: #ffffff;
    box-shadow: 0 0 6px rgba(255, 235, 140, 0.7);
    opacity: var(--brillo-musica);
    transform: translate3d(-50%, -50%, 0) scale(var(--escala-musica));
    will-change: transform, opacity;
    transition:
      transform 0.08s ease-out,
      opacity 0.08s ease-out;
  }

  .estrella-diamante {
    background: #ffffff;
    box-shadow: 0 0 10px rgba(255, 240, 160, 0.9);
  }

  /* Estrellas fugaces */
  .estrella-fugaz {
    position: absolute;
    width: 120px;
    height: 2px;
    background: linear-gradient(90deg, #ffffff, rgba(255, 235, 140, 0.6), transparent);
    opacity: 0;
    transform: rotate(-35deg);
    filter: drop-shadow(0 0 6px #fff);
  }

  .fugaz-1 {
    top: 15%;
    left: 70%;
    animation: cruzarFugaz 9s ease-in infinite;
    animation-delay: 3s;
  }

  .fugaz-2 {
    top: 32%;
    left: 40%;
    animation: cruzarFugaz 13s ease-in infinite;
    animation-delay: 8s;
  }

  @keyframes cruzarFugaz {
    0% {
      opacity: 0;
      transform: translate(0, 0) rotate(-35deg) scale(0.6);
    }
    5% {
      opacity: 0.95;
    }
    15% {
      opacity: 0;
      transform: translate(-220px, 140px) rotate(-35deg) scale(1.2);
    }
    100% {
      opacity: 0;
      transform: translate(-220px, 140px) rotate(-35deg);
    }
  }

  /* Sol cayendo pausadamente desde la mitad de la pantalla */
  .sol-crepusculo {
    position: absolute;
    top: 48%; /* Empieza desde la mitad de la pantalla */
    left: 50%;
    transform: translate(-50%, -50%);
    width: 150px;
    height: 150px;
    pointer-events: none;
    z-index: 2;
    animation: ocultarSolLento 9s cubic-bezier(0.25, 0.1, 0.25, 1) forwards;
  }

  .sol-disco {
    position: absolute;
    inset: 18px;
    border-radius: 50%;
    background: radial-gradient(circle, #ffe89e 0%, #f5b031 55%, #df6d22 100%);
    box-shadow:
      0 0 50px rgba(245, 176, 49, 0.75),
      0 0 90px rgba(223, 109, 34, 0.4);
  }

  .sol-corona {
    position: absolute;
    inset: -15px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(245, 176, 49, 0.4) 0%, transparent 75%);
    filter: blur(18px);
  }

  @keyframes ocultarSolLento {
    0% {
      transform: translate(-50%, -50%);
      opacity: 1;
    }
    40% {
      opacity: 0.95;
    }
    100% {
      transform: translate(-50%, 140px);
      opacity: 0.08;
    }
  }

  /* Luna poética con halo */
  .luna-ghibli {
    position: absolute;
    top: 65px;
    right: 50px;
    width: 68px;
    height: 68px;
    pointer-events: none;
    z-index: 2;
    animation: emergerLuna 3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  }

  .luna-disco {
    position: absolute;
    inset: 10px;
    border-radius: 50%;
    background: radial-gradient(circle at 35% 35%, #fffdf2 0%, #fcedb6 70%, #ffd978 100%);
    box-shadow:
      0 0 20px rgba(255, 243, 168, 0.6),
      0 0 45px rgba(255, 225, 120, 0.3);
  }

  .luna-halo {
    position: absolute;
    inset: -12px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255, 243, 168, 0.25) 0%, transparent 70%);
    filter: blur(12px);
    animation: pulsoLuna 4s ease-in-out infinite alternate;
  }

  @keyframes emergerLuna {
    0% {
      opacity: 0;
      transform: translateY(20px) scale(0.85);
    }
    100% {
      opacity: 0.95;
      transform: translateY(0) scale(1);
    }
  }

  @keyframes pulsoLuna {
    0% { opacity: 0.5; transform: scale(0.95); }
    100% { opacity: 0.9; transform: scale(1.1); }
  }

  .resplandor-horizonte {
    position: absolute;
    bottom: 12%;
    left: 50%;
    transform: translateX(-50%);
    width: 100vw;
    height: 180px;
    background: radial-gradient(
      ellipse at 50% 100%,
      rgba(242, 177, 53, 0.18) 0%,
      rgba(154, 98, 166, 0.12) 45%,
      transparent 80%
    );
    pointer-events: none;
    filter: blur(25px);
  }

  @media (max-width: 480px) {
    .luna-ghibli {
      top: 45px;
      right: 25px;
      width: 52px;
      height: 52px;
    }
    .sol-crepusculo {
      bottom: 20%;
      width: 110px;
      height: 110px;
    }
  }
</style>
